<template>
  <v-card>
    <loader v-if="fetchCompanyPending"/>
    <template v-else>
      <v-card-text class="pa-5">
        <v-container class="pa-0" grid-list-md>
          <v-row>
            <v-col :cols="12">
              <v-text-field
                v-model="form.name"
                name="companyName"
                :label="$t('texts.nameOfCompany')"
                disabled
                required
                :error-messages="errors.collect('name')"
                v-validate="'required'"
                data-vv-name="name"
                :data-vv-as="$t('texts.nameOfCompany')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12" :md="8">
              <v-text-field
                name="companyStreet"
                :label="$t('texts.street')"
                v-model="form.street"
                required
                :error-messages="errors.collect('street')"
                v-validate="'required'"
                data-vv-name="street"
                :data-vv-as="$t('texts.street')"
              />
            </v-col>
            <v-col :cols="12" :md="4">
              <v-text-field
                name="companyStreetNumber"
                :label="$t('texts.streetNumber')"
                v-model="form.street_number"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12" :md="4">
              <v-text-field
                name="companyZip"
                :label="$t('texts.zip')"
                value=""
                v-model="form.zip"
                required
                :error-messages="errors.collect('zip')"
                v-validate="'required'"
                data-vv-name="zip"
                :data-vv-as="$t('texts.zip')"
              />
            </v-col>
            <v-col :cols="12" :md="8">
              <v-text-field
                name="companyPlace"
                :label="$t('texts.place')"
                v-model="form.place"
                required
                :error-messages="errors.collect('place')"
                v-validate="'required'"
                data-vv-name="place"
                :data-vv-as="$t('texts.place')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <v-autocomplete
                :items="countries"
                v-model="form.country"
                :label="$t('texts.country')"
                item-value="iso"
                item-text="name"
                required
                :error-messages="errors.collect('country')"
                v-validate="'required'"
                data-vv-name="country"
                :data-vv-as="$t('texts.country')"
                autocomplete="off"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <v-text-field
                name="companyPhone"
                :label="$t('texts.phone')"
                v-model="form.phone"
                v-mask="mask.phoneNumber"
                required
                :error-messages="errors.collect('phone')"
                v-validate="{
                  required: true,
                  regex: /^(\+41\ )[1-9]{2}\ [0-9]{3}\ [0-9]{2}\ [0-9]{2}$/,
                }"
                :hint="form.phone === '' ? '+41 12 345 67 89' : ''"
                :persistent-hint="form.phone === ''"
                data-vv-name="phone"
                :data-vv-as="$t('texts.phone')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <v-text-field
                name="companyWebsite"
                :label="$t('texts.website')"
                v-model="form.website"
                type="url"
                :error-messages="errors.collect('website')"
                v-validate="''"
                data-vv-name="website"
                :data-vv-as="$t('texts.website')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <v-text-field
                name="companyEmail"
                :label="$t('texts.email')"
                v-model="form.email"
                type="email"
                required
                :error-messages="errors.collect('email')"
                v-validate="'required|email'"
                data-vv-name="email"
                :data-vv-as="$t('texts.email')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <v-select
                :items="companySizes"
                v-model="form.size"
                :label="$t('texts.numberOfEmployees')"
                :data-vv-as="$t('texts.numberOfEmployees')"
                data-vv-name="size"
                v-validate="'required'"
                :error-messages="errors.collect('size')"
                required
              >
              <template
                slot="item"
                slot-scope="{item}"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ $t('texts.company.sizes.' + item) }}</v-list-item-title>
                </v-list-item-content>
              </template>
              <template
                slot="selection"
                slot-scope="{item}"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ $t('texts.company.sizes.' + item) }}</v-list-item-title>
                </v-list-item-content>
              </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12" class="text-right">
              <v-btn
                outlined
                small
                light
                @click="updateLocation()"
                :loading="fetchLocationPending"
                :disabled="fetchLocationPending || storeCompanyPending"
              >
                <v-icon left>refresh</v-icon>
                {{ $t('texts.refreshlocation') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <map-component
                v-if="coordinates[0].lat && coordinates[0].lng"
                :mapName="`map-company-${id}`"
                :markers="coordinates"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider/>
      <v-card-actions class="px-5 py-3">
        <cta-buy-subscription/>
        <v-spacer/>
        <v-btn
          color="primary"
          class="white--text"
          @click.stop.prevent="validateBeforeSubmit()"
          :disabled="fetchLocationPending || storeCompanyPending"
          :loading="fetchLocationPending || storeCompanyPending"
        >
          {{ $t('texts.save') }}
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import CtaBuySubscription from '@/components/_molecules/company/CTABuySubscription.vue';
import MapComponent from '@/components/_organisms/MapComponent.vue';

const { mapState: mapGeneralState } = createNamespacedHelpers('general');
const { mapState: mapCompanyState } = createNamespacedHelpers('company');
const { mapState: mapCompanyProfileState, mapActions: mapCompanyProfileActions } = createNamespacedHelpers('company/profile');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'CompanyProfile',
  components: {
    CtaBuySubscription,
    MapComponent,
  },
  data() {
    return {
      id: this.$route.params.companyId,
      form: {
        name: '',
        street: '',
        street_number: '',
        zip: null,
        place: '',
        country: '',
        phone: '',
        website: '',
        email: '',
        size: null,
        motto: '',
        short_description: '',
        high_season: '',
        latitude: null,
        longitude: null,
      },
      mask: {
        phoneNumber: '+41 ## ### ## ##',
      },
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  created() {
    this.fetchCompany(this.$route.params.companyId).then(() => {
      Object.assign(this.form, this.company);
      this.$validator.reset();
    });
  },
  computed: {
    ...mapGeneralState([
      'countries',
    ]),
    ...mapCompanyState([
      'companySizes',
    ]),
    ...mapCompanyProfileState([
      'fetchCompanyPending',
      'storeCompanyPending',
      'fetchLocationPending',
      'company',
    ]),
    coordinates() {
      const coordinates = [{
        lat: parseFloat(this.form.latitude),
        lng: parseFloat(this.form.longitude),
      }];
      return coordinates;
    },
  },
  methods: {
    ...mapCompanyProfileActions([
      'fetchCompany',
      'storeCompany',
      'fetchLocation',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    updateLocation() {
      const payload = [
        this.form.street,
        this.form.street_number,
        this.form.zip,
        this.form.place,
        this.form.country,
      ];
      this.fetchLocation({ address: payload }).then((response) => {
        this.form.latitude = response.lat;
        this.form.longitude = response.lng;
        this.validateBeforeSubmit();
      });
    },
    validateBeforeSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$gtm.trackEvent({
            event: null, // Event type [default = 'interaction'] (Optional)
            category: 'Save Profile',
            action: 'click',
            label: 'Company',
            value: 1,
            noninteraction: false, // Optional
          });
          this.storeCompany(this.form).then((response) => {
            Object.assign(this.form, response);
          });
        } else {
          this.showMessageValidateError();
        }
      });
    },
  },
};
</script>
